import { useState } from 'react'
import Button from '../../shared/components/Button'
import { Select } from '../../shared/components'
import {
  useSolutions, useCreateSolution, useUpdateSolution, useDeactivateSolution,
  useIndustriesMeta, useCreateIndustry, useUpdateIndustry, useDeactivateIndustry,
  useBusinessCategoriesMeta, useCreateBusinessCategory, useUpdateBusinessCategory, useDeactivateBusinessCategory,
} from '../metadata/hooks/useMetadata'
import { useAttributeSchemas, useDeleteAttributeSchema } from './hooks/useAttributeSchemas'
import { AttributeSchemaModal } from './modals/AttributeSchemaModal'
import { useMerchants, useUpdateMerchant } from '../merchants/hooks/useMerchants'
import type { Solution, Industry, BusinessCategory } from '../metadata/api/metadataApi'
import type { Merchant } from '../merchants/types'
import type { AttributeSchema, SolutionId } from './types'
import { SOLUTION_IDS } from './types'
import { useAuthStore } from '../auth/store'
import { DropdownProvider } from '../../shared/utils/DropdownContext'

const TABS = ['Solutions', 'Industries', 'Business Categories', 'Attribute Schemas', 'Tenants'] as const
type Tab = typeof TABS[number]

// ── Shared ────────────────────────────────────────────────────────────────────

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      isActive ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'
    }`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-100 bg-red-50 p-8 text-center">
      <p className="text-sm font-medium text-red-700">{message}</p>
      <p className="text-xs text-red-500 mt-1">Check that the upstream service is running and reachable.</p>
    </div>
  )
}

// ── Solutions Tab ─────────────────────────────────────────────────────────────

function SolutionForm({
  solution,
  onClose,
}: {
  solution?: Solution | null
  onClose: () => void
}) {
  const isEdit = !!solution
  const createMutation = useCreateSolution()
  const updateMutation = useUpdateSolution()

  const [slug, setSlug] = useState(solution?.slug ?? '')
  const [name, setName] = useState(solution?.name ?? '')
  const [description, setDescription] = useState(solution?.description ?? '')
  const [error, setError] = useState('')

  const isPending = createMutation.isPending || updateMutation.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    try {
      if (isEdit && solution) {
        await updateMutation.mutateAsync({ slug: solution.slug, data: { name, description: description || undefined } })
      } else {
        await createMutation.mutateAsync({ slug, name, description: description || undefined })
      }
      onClose()
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-md">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">{isEdit ? 'Edit Solution' : 'Create Solution'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Slug <span className="text-red-500">*</span></label>
              <input
                required
                placeholder="e.g. rack-and-box"
                className="w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring font-mono"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              />
              <p className="text-xs text-muted-foreground mt-1">Unique identifier — cannot be changed after creation.</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Name <span className="text-red-500">*</span></label>
            <input
              required
              placeholder="e.g. Rack & Box"
              className="w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              rows={3}
              placeholder="Optional description"
              className="w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={isPending} className="flex-1">
              {isEdit ? 'Save Changes' : 'Create Solution'}
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded-md py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function SolutionsTab() {
  const { data: solutions = [], isLoading, error } = useSolutions()
  const deactivate = useDeactivateSolution()
  const [formTarget, setFormTarget] = useState<Solution | null | 'new'>()

  if (isLoading) return <div className="text-sm text-muted-foreground py-6">Loading solutions…</div>
  if (error) return <ErrorState message="Failed to load solutions" />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{solutions.length} solution{solutions.length !== 1 ? 's' : ''}</p>
        <Button size="sm" onClick={() => setFormTarget('new')}>+ New Solution</Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b">
            <tr>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Slug</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Description</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {solutions.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-muted-foreground text-sm">No solutions yet.</td></tr>
            ) : solutions.map((s) => (
              <tr key={s.id} className="hover:bg-muted">
                <td className="px-4 py-3 font-mono text-xs text-foreground">{s.slug}</td>
                <td className="px-4 py-3 font-medium text-foreground">{s.name}</td>
                <td className="px-4 py-3 text-muted-foreground whitespace-pre-wrap">{s.description || <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-3"><StatusBadge isActive={s.isActive} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => setFormTarget(s)} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
                    {s.isActive && (
                      <button
                        onClick={() => { if (window.confirm(`Deactivate "${s.name}"?`)) deactivate.mutate(s.slug) }}
                        disabled={deactivate.isPending}
                        className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formTarget && (
        <SolutionForm
          solution={formTarget === 'new' ? null : formTarget}
          onClose={() => setFormTarget(undefined)}
        />
      )}
    </div>
  )
}

// ── Simple Name-only entity forms (Industries & Business Categories) ───────────

function NameEntityForm({
  entityName,
  initial,
  onSubmit,
  onClose,
  isPending,
}: {
  entityName: string
  initial?: string
  onSubmit: (name: string) => Promise<void>
  onClose: () => void
  isPending: boolean
}) {
  const [name, setName] = useState(initial ?? '')
  const [error, setError] = useState('')
  const isEdit = !!initial

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    try {
      await onSubmit(name)
      onClose()
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-sm">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">{isEdit ? `Edit ${entityName}` : `Create ${entityName}`}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Name <span className="text-red-500">*</span></label>
            <input
              required
              autoFocus
              className="w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={isPending} className="flex-1">
              {isEdit ? 'Save Changes' : `Create ${entityName}`}
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded-md py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Industries Tab ─────────────────────────────────────────────────────────────

function IndustriesTab() {
  const { data: industries = [], isLoading, error } = useIndustriesMeta()
  const createMutation = useCreateIndustry()
  const updateMutation = useUpdateIndustry()
  const deactivateMutation = useDeactivateIndustry()
  const [formTarget, setFormTarget] = useState<Industry | null | 'new'>()

  if (isLoading) return <div className="text-sm text-muted-foreground py-6">Loading industries…</div>
  if (error) return <ErrorState message="Failed to load industries" />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{industries.length} industr{industries.length !== 1 ? 'ies' : 'y'}</p>
        <Button size="sm" onClick={() => setFormTarget('new')}>+ New Industry</Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b">
            <tr>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {industries.length === 0 ? (
              <tr><td colSpan={3} className="text-center py-10 text-muted-foreground text-sm">No industries yet.</td></tr>
            ) : industries.map((ind) => (
              <tr key={ind.id} className="hover:bg-muted">
                <td className="px-4 py-3 font-medium text-foreground">{ind.name}</td>
                <td className="px-4 py-3"><StatusBadge isActive={ind.isActive} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => setFormTarget(ind)} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
                    {ind.isActive && (
                      <button
                        onClick={() => { if (window.confirm(`Deactivate "${ind.name}"?`)) deactivateMutation.mutate(ind.id) }}
                        disabled={deactivateMutation.isPending}
                        className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formTarget && (
        <NameEntityForm
          entityName="Industry"
          initial={formTarget === 'new' ? undefined : formTarget.name}
          isPending={createMutation.isPending || updateMutation.isPending}
          onSubmit={async (name) => {
            if (formTarget === 'new') {
              await createMutation.mutateAsync({ name })
            } else {
              await updateMutation.mutateAsync({ id: (formTarget as Industry).id, data: { name } })
            }
          }}
          onClose={() => setFormTarget(undefined)}
        />
      )}
    </div>
  )
}

// ── Business Categories Tab ────────────────────────────────────────────────────

function BusinessCategoriesTab() {
  const { data: categories = [], isLoading, error } = useBusinessCategoriesMeta()
  const createMutation = useCreateBusinessCategory()
  const updateMutation = useUpdateBusinessCategory()
  const deactivateMutation = useDeactivateBusinessCategory()
  const [formTarget, setFormTarget] = useState<BusinessCategory | null | 'new'>()

  if (isLoading) return <div className="text-sm text-muted-foreground py-6">Loading business categories…</div>
  if (error) return <ErrorState message="Failed to load business categories" />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}</p>
        <Button size="sm" onClick={() => setFormTarget('new')}>+ New Category</Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b">
            <tr>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {categories.length === 0 ? (
              <tr><td colSpan={3} className="text-center py-10 text-muted-foreground text-sm">No business categories yet.</td></tr>
            ) : categories.map((cat) => (
              <tr key={cat.id} className="hover:bg-muted">
                <td className="px-4 py-3 font-medium text-foreground">{cat.name}</td>
                <td className="px-4 py-3"><StatusBadge isActive={cat.isActive} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => setFormTarget(cat)} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
                    {cat.isActive && (
                      <button
                        onClick={() => { if (window.confirm(`Deactivate "${cat.name}"?`)) deactivateMutation.mutate(cat.id) }}
                        disabled={deactivateMutation.isPending}
                        className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40"
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formTarget && (
        <NameEntityForm
          entityName="Business Category"
          initial={formTarget === 'new' ? undefined : formTarget.name}
          isPending={createMutation.isPending || updateMutation.isPending}
          onSubmit={async (name) => {
            if (formTarget === 'new') {
              await createMutation.mutateAsync({ name })
            } else {
              await updateMutation.mutateAsync({ id: (formTarget as BusinessCategory).id, data: { name } })
            }
          }}
          onClose={() => setFormTarget(undefined)}
        />
      )}
    </div>
  )
}

// ── Attribute Schemas Tab ─────────────────────────────────────────────────────

const DATA_TYPE_BADGE: Record<string, string> = {
  ENUM: 'bg-blue-50 text-blue-700',
  COLOR: 'bg-purple-50 text-purple-700',
  STRING: 'bg-muted text-muted-foreground',
  NUMBER: 'bg-amber-50 text-amber-700',
}

const SOLUTION_OPTIONS = SOLUTION_IDS.map((s) => ({ title: s, value: s }))

function SchemaTable({
  title,
  schemas,
  deletingId,
  onEdit,
  onDelete,
}: {
  title: string
  schemas: AttributeSchema[]
  deletingId: string | null
  onEdit: (s: AttributeSchema) => void
  onDelete: (s: AttributeSchema) => void
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{title}</h3>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b">
            <tr>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Key</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Label</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Type</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Allowed Values</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Required</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Order</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {schemas
              .slice()
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((schema) => (
                <tr key={schema.id} className="hover:bg-muted">
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{schema.key}</td>
                  <td className="px-4 py-3 text-foreground">{schema.label}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${DATA_TYPE_BADGE[schema.dataType] ?? 'bg-muted text-muted-foreground'}`}>
                      {schema.dataType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">
                    {schema.allowedValues.length > 0 ? schema.allowedValues.join(', ') : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {schema.isRequired ? <span className="text-green-600 font-medium">Yes</span> : <span className="text-muted-foreground">No</span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{schema.sortOrder}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => onEdit(schema)} className="text-xs text-blue-600 hover:text-blue-800">Edit</button>
                      <button
                        onClick={() => onDelete(schema)}
                        disabled={deletingId === schema.id}
                        className="text-xs text-red-500 hover:text-red-700 disabled:opacity-40"
                      >
                        {deletingId === schema.id ? '…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function AttributeSchemasTab() {
  const [solutionId, setSolutionId] = useState<SolutionId>('rack-and-box')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingSchema, setEditingSchema] = useState<AttributeSchema | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const selectedTenantId = useAuthStore((state) => state.selectedTenantId)
  const { data: schemas = [], isLoading, error } = useAttributeSchemas(solutionId, selectedTenantId || undefined)
  const deleteMutation = useDeleteAttributeSchema()

  const solutionSchemas = schemas.filter((s) => !s.tenantId)
  const merchantSchemas = schemas.filter((s) => !!s.tenantId)

  async function handleDelete(schema: AttributeSchema) {
    if (!window.confirm(`Delete attribute schema "${schema.label}" (${schema.key})? This cannot be undone.`)) return
    setDeletingId(schema.id)
    try {
      await deleteMutation.mutateAsync({ id: schema.id, solutionId: schema.solutionId })
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">Solution:</span>
          <div className="w-52">
            <Select
              label=""
              options={SOLUTION_OPTIONS}
              value={solutionId}
              size="small"
              onChange={(v) => setSolutionId(v as SolutionId)}
            />
          </div>
        </div>
        <Button size="sm" onClick={() => { setEditingSchema(null); setIsModalOpen(true) }}>
          + New Schema
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground py-4">Loading schemas…</p>}

      {!isLoading && error && <ErrorState message="Failed to load attribute schemas" />}

      {!isLoading && !error && schemas.length === 0 && (
        <div className="border-2 border-dashed rounded-lg p-10 text-center text-muted-foreground">
          <p className="text-sm">No attribute schemas for <strong>{solutionId}</strong>.</p>
          <p className="text-xs mt-1">Create one to enable dynamic product fields for merchants.</p>
        </div>
      )}

      {solutionSchemas.length > 0 && (
        <SchemaTable
          title={`${solutionId} — solution-wide`}
          schemas={solutionSchemas}
          deletingId={deletingId}
          onEdit={(s) => { setEditingSchema(s); setIsModalOpen(true) }}
          onDelete={handleDelete}
        />
      )}

      {merchantSchemas.length > 0 && (
        <SchemaTable
          title="Merchant-specific overrides"
          schemas={merchantSchemas}
          deletingId={deletingId}
          onEdit={(s) => { setEditingSchema(s); setIsModalOpen(true) }}
          onDelete={handleDelete}
        />
      )}

      <AttributeSchemaModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingSchema(null) }}
        schema={editingSchema}
      />
    </div>
  )
}

// ── Tenants Tab ───────────────────────────────────────────────────────────────

function ReassignSolutionModal({
  merchant,
  solutions,
  onClose,
}: {
  merchant: Merchant
  solutions: Solution[]
  onClose: () => void
}) {
  const updateMutation = useUpdateMerchant()
  const [solutionId, setSolutionId] = useState(merchant.solutionId)
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    try {
      await updateMutation.mutateAsync({ id: merchant.id, data: { solutionId } })
      onClose()
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Something went wrong')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg w-full max-w-sm">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">Reassign Solution</h2>
          <p className="text-sm text-muted-foreground mt-1">{merchant.name}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Solution</label>
            <select
              className="w-full bg-background text-foreground placeholder:text-muted-foreground border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={solutionId}
              onChange={(e) => setSolutionId(e.target.value)}
            >
              {solutions.filter((s) => s.isActive).map((s) => (
                <option key={s.slug} value={s.slug}>{s.name}</option>
              ))}
            </select>
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={updateMutation.isPending} className="flex-1">Save</Button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded-md py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function TenantsTab() {
  const { data: merchants = [], isLoading, error } = useMerchants()
  const { data: solutions = [] } = useSolutions()
  const [filterSolution, setFilterSolution] = useState<string>('all')
  const [reassigning, setReassigning] = useState<Merchant | null>(null)

  if (isLoading) return <div className="text-sm text-muted-foreground py-6">Loading tenants…</div>
  if (error) return <ErrorState message="Failed to load tenants" />

  const solutionOptions = [
    { title: 'All Solutions', value: 'all' },
    ...solutions.map((s) => ({ title: s.name, value: s.slug })),
  ]

  const filtered = filterSolution === 'all'
    ? merchants
    : merchants.filter((m) => m.solutionId === filterSolution)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">Solution:</span>
          <div className="w-52">
            <Select
              label=""
              options={solutionOptions}
              value={filterSolution}
              size="small"
              onChange={(v) => setFilterSolution(v)}
            />
          </div>
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} tenant{filtered.length !== 1 ? 's' : ''}</p>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b">
            <tr>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Slug</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Solution</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-10 text-muted-foreground text-sm">No tenants found.</td></tr>
            ) : filtered.map((m) => {
              const solutionName = solutions.find((s) => s.slug === m.solutionId)?.name ?? m.solutionId
              return (
                <tr key={m.id} className="hover:bg-muted">
                  <td className="px-4 py-3 font-medium text-foreground">{m.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{m.slug}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                      {solutionName}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge isActive={m.active} /></td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setReassigning(m)}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Reassign
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {reassigning && (
        <ReassignSolutionModal
          merchant={reassigning}
          solutions={solutions}
          onClose={() => setReassigning(null)}
        />
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const TcsConfigPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>('Solutions')

  return (
    <DropdownProvider>
      <div className="py-6">
        <div className="mb-8">
          <h1 className="text-[23px] font-bold text-foreground">TCS Config</h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            Manage platform solutions, industries, business categories, attribute schemas and tenant assignments
          </p>
        </div>

        <div className="flex gap-1 border-b border-[#E5E7EB] mb-8 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-[13px] font-[600] transition-colors border-b-2 -mb-[2px] whitespace-nowrap ${
                activeTab === tab
                  ? 'border-[#3F3E42] text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'Solutions' && <SolutionsTab />}
        {activeTab === 'Industries' && <IndustriesTab />}
        {activeTab === 'Business Categories' && <BusinessCategoriesTab />}
        {activeTab === 'Attribute Schemas' && <AttributeSchemasTab />}
        {activeTab === 'Tenants' && <TenantsTab />}
      </div>
    </DropdownProvider>
  )
}
